<script setup>
import api from "@/api";
import leaflet from "leaflet";
import { ref, onMounted } from "vue";
import 'leaflet-fullscreen'
import 'leaflet-imageoverlay-rotated'
import { useStore } from "vuex"

const store = useStore()

const image_map = ref(null)

const props = defineProps({
  center: Object,
  zoom: {
    default: 16
  },
  modelValue: {
    type: Object,
    default: {lat: 35.6996774, lng: 51.3376701, address: '', points: []}
  },
  factory: Object,
  edit_image: Boolean,
  nopan: Boolean,
})

const emit = defineEmits(['update:modelValue', 'update_factory'])

let map;
const image = ref(null);
var imageOverlay;
var first_point, first_marker, second_point, second_marker, three_point, three_marker;
var factory_location;

const setImageLatLng = () => {
  first_point = [first_marker.getLatLng().lat, first_marker.getLatLng().lng]
  second_point = [second_marker.getLatLng().lat, second_marker.getLatLng().lng]
  three_point = [three_marker.getLatLng().lat, three_marker.getLatLng().lng]
  props.modelValue.points = [first_point, second_point, three_point]
  imageOverlay.reposition(first_point, second_point, three_point)
}

const addImage = async () => {
  first_point = props.factory?.array_point && props.factory.array_point[0] ? props.factory.array_point[0] : [factory_location.lat+0.001, factory_location.lng-0.001]
  second_point = props.factory?.array_point && props.factory.array_point[1] ? props.factory.array_point[1] : [factory_location.lat+0.001, factory_location.lng+0.001]
  three_point = props.factory?.array_point && props.factory.array_point[2] ? props.factory.array_point[2] : [factory_location.lat-0.001, factory_location.lng-0.001]
  props.modelValue.points = [first_point, second_point, three_point]
  imageOverlay = leaflet.imageOverlay.rotated(image.value, first_point, second_point, three_point, {
    opacity: 0.6,
    alt: props.factory.name,
    interactive: true
  }).addTo(map);
  if (props.edit_image) {
    let svgMultipleTemplate = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" xmlns:v="https://vecta.io/nano"><path d="M16 12h7v1h-7zM2 13h7v-1H2zm10 10h1v-7h-1zm0-14h1V2h-1zm-2 1h5v5h-5zm1 4h3v-3h-3z"/></svg>'
    const iconMultiple = leaflet.divIcon({
      className: "virtual-marker",
      html: svgMultipleTemplate,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [7, -16]
    });

    first_marker = leaflet.marker(first_point, {
      icon: iconMultiple,
      draggable: true,
      autoPan: true,
    }).addTo(map)
    second_marker = leaflet.marker(second_point, {
      icon: iconMultiple,
      draggable: true,
      autoPan: true,
    }).addTo(map)
    three_marker = leaflet.marker(three_point, {
      icon: iconMultiple,
      draggable: true,
      autoPan: true,
    }).addTo(map)

    setImageLatLng()

    first_marker.on("move", () => {
      setImageLatLng()
    })
    second_marker.on("move", () => {
      setImageLatLng()
    })
    three_marker.on("move", () => {
      setImageLatLng()
    })
  }
}

const removeImage = () => {
  if (imageOverlay) imageOverlay.remove();
  if (first_marker) first_marker.remove();
  if (second_marker) second_marker.remove();
  if (three_marker) three_marker.remove();
  emit('update_factory')
}

onMounted(async () => {
  let config = {
    minZoom: 3,
    maxZoom: 23,
    fullscreenControl: true,
    drawControl: true
  };

  factory_location = {lat: 41.009123, lng: 28.965958}
  let default_location = false

  if (props.center) {
    factory_location = props.center
    default_location = true
  } else {
    if (props.factory?.location) {
      let detail_location = await api.readLocation(props.factory.location.id)
      factory_location = {lat: detail_location.data.lat, lng: detail_location.data.lon}
      default_location = true
    }
  }

  map = leaflet.map('map', config).setView(factory_location, props.zoom);

  leaflet.tileLayer('http://{s}.tile.osm.org/{z}/{x}/{y}.png', {
    maxZoom:19,
    minZoom: 1,
    attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
  }).addTo(map);

  let marker;

  if (default_location) {
    marker = leaflet.marker(factory_location, {}).addTo(map);
  } else {
    navigator.geolocation.getCurrentPosition(
      (e) => {
        map.setView({lat: e.coords.latitude, lng: e.coords.longitude})
        marker = leaflet.marker(factory_location).addTo(map);
      },
      () => {
        marker = leaflet.marker(factory_location).addTo(map);
      }
    )
  }

  image.value = props.factory.image
  if (image.value) addImage()

  const legend = leaflet.control({ position: "bottomleft" });

  legend.onAdd = function () {
    let div = leaflet.DomUtil.create("div", "description");
    div.style.fontFamily = "'SF UI Display', Vazir, sans-serif"
    div.setAttribute('dir', 'auto')
    leaflet.DomEvent.disableClickPropagation(div);
    const text = '';
    div.insertAdjacentHTML("beforeend", text);
    return div;
  };

  legend.addTo(map);

  const homeTemplate = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19 9L19 17C19 18.8856 19 19.8284 18.4142 20.4142C17.8284 21 16.8856 21 15 21L14 21L10 21L9 21C7.11438 21 6.17157 21 5.58579 20.4142C5 19.8284 5 18.8856 5 17L5 9" stroke="#323232" stroke-width="2" stroke-linejoin="round"/><path d="M3 11L7.5 7L10.6713 4.18109C11.429 3.50752 12.571 3.50752 13.3287 4.18109L16.5 7L21 11" stroke="#323232" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M10 21V17C10 15.8954 10.8954 15 12 15V15C13.1046 15 14 15.8954 14 17V21" stroke="#323232" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const homeControl = leaflet.Control.extend({
    options: {
      position: "bottomleft",
    },

    onAdd: function () {
      const btn = leaflet.DomUtil.create("button");
      btn.title = "back to home";
      btn.innerHTML = homeTemplate;
      btn.className += "leaflet-bar back-to-home center-location cursor-pointer bg-white";
      btn.style = "padding: 5px 5px 0 5px"

      return btn;
    },
  });

  map.addControl(new homeControl());

  const buttonBackToHome = document.querySelector(".center-location");

  props.modelValue.lat = factory_location.lat
  props.modelValue.lng = factory_location.lng
  if(!props.nopan) {
    map.on("move", () => {
      let center = map.getCenter()
      if (marker) {
        marker.setLatLng(center)
      }
      props.modelValue.lat = center.lat
      props.modelValue.lng = center.lng
    })
  } else {
    map.on("click", event => {
      if (marker) {
        marker.setLatLng(event.latlng)
      }
      props.modelValue.lat = event.latlng.lat
      props.modelValue.lng = event.latlng.lng
    })
  }

  const markerInfo = document.querySelector(".description");
  let center = map.getCenter()
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  fetch(`https://nominatim.openstreetmap.org/reverse?format=json&accept-language=${timezone}&lon=${center.lng}&lat=${center.lat}`).then(res => {
    return res.json()
  }).then(data => {
    props.modelValue.address = data.display_name
    markerInfo.textContent = data.display_name
  }).catch(() => {
    //
  })
  map.on("moveend", () => {
    center = map.getCenter()
    fetch(`https://nominatim.openstreetmap.org/reverse?format=json&accept-language=${timezone}&lon=${center.lng}&lat=${center.lat}`).then(res => {
      return res.json()
    }).then(data => {
      props.modelValue.address = data.display_name
      markerInfo.textContent = data.display_name
    })
  })

  buttonBackToHome.addEventListener("click", (event) => {
    event.stopPropagation()
    map.setView(factory_location)
    marker.setLatLng(factory_location)
  });

  if (props.edit_image) {
    const addImageTemplate = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns:v="https://vecta.io/nano"><path d="M12.5 3H7.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C3 5.28 3 6.12 3 7.8v8.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C5.28 21 6.12 21 7.8 21H17c.93 0 1.395 0 1.776-.102a3 3 0 0 0 2.121-2.121C21 18.395 21 17.93 21 17m-2-9V2m-3 3h6M10.5 8.5a2 2 0 1 1-4 0 2 2 0 1 1 4 0zm4.49 3.418l-8.459 7.69c-.476.433-.714.649-.735.836a.5.5 0 0 0 .167.431c.142.125.463.125 1.106.125h9.387c1.439 0 2.159 0 2.724-.242a3 3 0 0 0 1.578-1.578c.242-.565.242-1.285.242-2.724 0-.484 0-.726-.053-.952a2 2 0 0 0-.374-.778c-.143-.182-.332-.334-.71-.636l-2.797-2.238c-.378-.303-.568-.454-.776-.508a1 1 0 0 0-.557.018c-.205.066-.384.229-.742.555z" stroke="#000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    const removeImageTemplate = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns:v="https://vecta.io/nano"><path d="M16.5 2.5l5 5m0-5l-5 5m-4-4.5H7.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C3 5.28 3 6.12 3 7.8v8.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C5.28 21 6.12 21 7.8 21H17c.93 0 1.395 0 1.776-.102a3 3 0 0 0 2.121-2.121C21 18.395 21 17.93 21 17M10.5 8.5a2 2 0 1 1-4 0 2 2 0 1 1 4 0zm4.49 3.418l-8.459 7.69c-.476.433-.714.649-.735.836a.5.5 0 0 0 .167.431c.142.125.463.125 1.106.125h9.387c1.439 0 2.159 0 2.724-.242a3 3 0 0 0 1.578-1.578c.242-.565.242-1.285.242-2.724 0-.484 0-.726-.053-.952a2 2 0 0 0-.374-.778c-.143-.182-.332-.334-.71-.636l-2.797-2.238c-.378-.303-.568-.454-.776-.508a1 1 0 0 0-.557.018c-.205.066-.384.229-.742.555z" stroke="#000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    const addImageControl = leaflet.Control.extend({
      options: {
        position: "bottomleft",
      },

      onAdd: function () {
        const btn = leaflet.DomUtil.create("button");
        btn.title = image.value ? "Remove image on map" : "Add image on map";
        btn.innerHTML = image.value ? removeImageTemplate : addImageTemplate;
        btn.className += "leaflet-bar cursor-pointer bg-white";
        btn.style = "padding: 5px 5px 0;"
        btn.addEventListener('click', (event) => {
          event.stopPropagation()
          if (image.value) {
            api.removeFactoryImage(store.state.factoryInfo.name).then(() => {
              image.value = false
              btn.innerHTML = addImageTemplate
              btn.title = "Add image on map"
              removeImage()
            })
          } else image_map.value.click()
        })

        return btn;
      },
    });

    map.addControl(new addImageControl());
  }
})

const setImage = (e) => {
  api.uploadFactoryMapImage(e.target.files[0], store.state.factoryInfo.name).then(res => {
    image.value = res.data.image
    addImage()
    emit('update_factory')
  })
}
</script>

<template>
  <div>
    <v-card id="map" style="height: 320px"></v-card>
    <input type="file" accept=".jpg, .jpeg, .png, .gif" ref="image_map" @change="setImage" hidden>
  </div>
</template>